import { useIntl } from "react-intl";

import { LazyImage } from "@/components/Image/LazyImage";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import { HotelStars } from "@/features/hotels/components/hotels/HotelStars";
import type { HotelData } from "@/features/hotels/types/types";
import { formatCurrency } from "@/utils/currency";

type Props = {
  hotel: HotelData;
  onClick?: () => void;
  bookingNights: number;
};

export const HotelCard = ({ hotel, onClick, bookingNights }: Props) => {
  const intl = useIntl();

  return (
    <button
      type="button"
      className="flex w-full flex-col gap-4 rounded-3xl bg-white p-1 text-start"
      onClick={onClick}
    >
      <div className="flex w-full justify-between">
        <div className="flex w-[130px] shrink-0 items-center overflow-clip rounded-s-2xl bg-gray-100 text-center text-xs text-text-tertiary">
          <LazyImage
            className="size-full object-cover contain-size"
            sentryElementClassName="self-start"
            src={hotel.mainImageUrl || "/imagePlaceholder.svg"}
            alt={
              hotel.name ||
              intl.formatMessage({
                defaultMessage: "No image available",
                description:
                  "Fallback alt text shown when no hotel or room image is available",
              })
            }
          />
        </div>

        <div className="flex w-full flex-col p-3">
          <span className="mb-1 line-clamp-2 text-base font-medium text-text-title">
            {hotel.name}
          </span>

          {!!hotel.stars && (
            <HotelStars stars={hotel.stars} showLabel={false} />
          )}

          {hotel.specialDeal || hotel.recommended ? (
            <div className="mt-3 mb-auto flex gap-3">
              {hotel.specialDeal ? (
                <div className="flex items-center">
                  <span className="rounded bg-core-caution-100 px-1.5 py-1 text-sm font-semibold text-semantic-warning-strong">
                    {intl.formatMessage({
                      defaultMessage: "Special Deal",
                      description:
                        "Badge on hotel cards indicating a special promotional deal",
                    })}
                  </span>
                </div>
              ) : null}
              {hotel.recommended ? (
                <span className="rounded bg-surface-accent-softer px-1.5 py-1 text-sm font-semibold text-core-accent-700">
                  {intl.formatMessage({
                    defaultMessage: "Rahal's Choice",
                    description: "Rahal's Choice badge label",
                  })}
                </span>
              ) : null}
            </div>
          ) : null}

          {hotel.minPrice ? (
            <div className="mt-6 text-end">
              <div className="text-sm text-text-tertiary">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      "Total for {nights, plural, one {# night} other {# nights}}",
                    description:
                      "Label indicating the total price is for a certain number of nights",
                  },
                  { nights: bookingNights },
                )}
              </div>
              <div className="text-base font-bold text-surface-accent-strong">
                {formatCurrency(hotel.minPrice.value, hotel.minPrice.currency)}
              </div>
              <div className="text-sm text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage: "Includes taxes & fees",
                  description:
                    "Label indicating that the displayed price includes taxes and fees",
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
};

export const HotelCardSkeleton = () => {
  return (
    <div className="flex w-full flex-col gap-4 rounded-3xl bg-white p-1 text-start">
      <div className="flex w-full justify-between">
        <div className="flex w-[130px] shrink-0 items-center overflow-clip rounded-s-2xl">
          <Skeleton width="100%" height="100%" variant="rectangular" />
        </div>

        <div className="flex w-full flex-col gap-1 p-3">
          <Skeleton width="100%" height={48} />

          <Skeleton width={55} height={13} />

          <div className="mt-6 flex w-full flex-col items-end gap-0.5">
            <Skeleton width={110} height={18} />
            <Skeleton width={130} height={24} />
            <Skeleton width={120} height={18} />
          </div>
        </div>
      </div>
    </div>
  );
};
